import { useRef, useCallback } from 'react';
import { ShoppingCart, ChevronUp } from 'lucide-react';
import { formatPrice, cn } from '../lib/utils';
import type { Mode } from '../lib/types';

interface CartBarProps {
  mode: Mode;
  itemCount: number;
  total: number;
  parkedCount: number;
  onExpand: () => void;
  onLongPress: () => void;
  variant: 'phone' | 'tablet';
}

export function CartBar({
  mode,
  itemCount,
  total,
  parkedCount,
  onExpand,
  onLongPress,
  variant,
}: CartBarProps) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPress = useRef(false);

  const handleTouchStart = useCallback(() => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      onLongPress();
    }, 500);
  }, [onLongPress]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (!isLongPress.current) {
      onExpand();
    }
  }, [onExpand]);

  const handleTouchMove = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  if (itemCount === 0) return null;

  const isPhone = variant === 'phone';

  return (
    <div
      className={cn(
        'animate-slide-up',
        isPhone
          ? 'fixed left-0 right-0 z-[39]'
          : 'absolute bottom-0 left-0 right-0 z-10'
      )}
      style={isPhone ? { bottom: '7.5rem' } : undefined}
    >
      <div
        className={cn(
          'mx-4 mb-2 flex items-center justify-between rounded-2xl border bg-card/95 px-4 py-3 shadow-lg backdrop-blur-sm cursor-pointer select-none',
          mode === 'buy' ? 'border-tertiary/40' : 'border-primary/40'
        )}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={(e) => {
          // Desktop click (no touch events)
          if (!('ontouchstart' in window)) {
            onExpand();
          }
          e.stopPropagation();
        }}
      >
        <div className="flex items-center gap-3">
          <ShoppingCart
            className={cn(
              'h-5 w-5',
              mode === 'buy' ? 'text-tertiary' : 'text-primary'
            )}
          />
          <span className="text-sm font-medium text-card-foreground">
            {itemCount} card{itemCount !== 1 ? 's' : ''} &mdash;{' '}
            <span
              className={cn(
                'font-semibold',
                mode === 'buy' ? 'text-tertiary' : 'text-primary'
              )}
            >
              {formatPrice(total)}
            </span>
          </span>
          {parkedCount > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              +{parkedCount} parked
            </span>
          )}
        </div>
        <ChevronUp className="h-5 w-5 text-muted-foreground" />
      </div>
    </div>
  );
}
